import React, { useEffect, useMemo, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import {
  createDefaultAdminListTimeRange,
  parseReleaseRegressionQueryParams,
} from "../../contracts/admin-route-params";
import {
  formatAdminDashboardSummary,
  pickAdminDashboardLocaleText,
} from "../../shared/locale/admin-dashboard-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import { AdminAiRuntimeSummaryStrip } from "../../shared/ui/admin-ai-runtime-summary-strip";
import { AdminAggregateSummaryStrip } from "../../shared/ui/admin-aggregate-summary-strip";
import { AdminSourceStatusGrid } from "../../shared/ui/admin-source-status-grid";
import {
  buildAdminStateRouteMeta,
  buildAdminStateScopeMeta,
  buildAdminStateTimeRangeMeta,
} from "../../shared/ui/admin-screen-state-meta";
import {
  AdminStatusBadge,
  toAdminFreshnessBadgeValue,
} from "../../shared/ui/admin-status-badge";
import {
  clearHiddenAdminListCursorSearchParams,
  clearExplicitAdminTimeRangeSearchParams,
  resolveExplicitAdminTimeRangeUpperBound,
  resolveAdminLiveTimeRangeRefreshSeed,
  useAdminAutoRefreshTick,
} from "../../shared/query/admin-auto-refresh";
import { AdminLiveRangeBanner } from "../../shared/ui/admin-live-range-banner";
import { useReleaseRegressionScreenModel } from "./use-release-regression-screen-model";

export type ReleaseRegressionScreenProps = {
  dependencies: AdminDashboardDependencies;
};

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

function baseControlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

function createNextSearchParams(formData: FormData, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of ["from", "to", "release", "appVersion", "platform"]) {
    const rawValue = formData.get(key);
    const value = typeof rawValue === "string" ? rawValue.trim() : "";

    if (value.length > 0) {
      params.set(key, value);
    }
  }

  return withAdminDashboardLocaleSearchParams(params, locale);
}

function formatReleaseScopeSummary(
  params: URLSearchParams,
  locale: "ko" | "en",
): string | undefined {
  return formatAdminDashboardSummary(locale, [
    { key: "release", value: params.get("release") },
    { key: "app", value: params.get("appVersion") },
    { key: "platform", value: params.get("platform") },
  ]);
}

function buildReleaseStateMeta(
  locale: "ko" | "en",
  query: {
    timeRange: {
      from: string;
      to: string;
    };
  },
  scopeSummary: string | undefined,
) {
  return [
    buildAdminStateTimeRangeMeta(locale, query.timeRange.from, query.timeRange.to),
    buildAdminStateScopeMeta(locale, scopeSummary),
    buildAdminStateRouteMeta(locale, "/admin/releases"),
  ].filter((item): item is NonNullable<typeof item> => item !== undefined);
}

export function ReleaseRegressionScreen(props: ReleaseRegressionScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const refreshTick = useAdminAutoRefreshTick(props.dependencies.runtimeConfig);
  const effectiveSearchParams = useMemo(
    () => clearHiddenAdminListCursorSearchParams(searchParams),
    [searchParams],
  );
  const searchParamsKey = effectiveSearchParams.toString();
  const defaultTimeRangeRefreshSeed = resolveAdminLiveTimeRangeRefreshSeed(effectiveSearchParams, refreshTick);
  const explicitUpperBound = resolveExplicitAdminTimeRangeUpperBound(effectiveSearchParams);
  const defaultTimeRange = useMemo(
    () => createDefaultAdminListTimeRange(),
    [defaultTimeRangeRefreshSeed],
  );
  const query = useMemo(
    () => parseReleaseRegressionQueryParams(effectiveSearchParams, defaultTimeRange),
    [defaultTimeRange, effectiveSearchParams, searchParamsKey],
  );
  const scopeSummary = formatReleaseScopeSummary(effectiveSearchParams, locale);

  useEffect(() => {
    if (effectiveSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(withAdminDashboardLocaleSearchParams(effectiveSearchParams, locale), {
      replace: true,
    });
  }, [effectiveSearchParams, locale, searchParams, setSearchParams]);
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
      query,
    }),
    [props.dependencies, locale, query],
  );
  const state = useReleaseRegressionScreenModel(screenModelDependencies);
  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Releases", ko: "릴리스" }),
    title: pickAdminDashboardLocaleText(locale, { en: "Release Regression", ko: "릴리스 회귀" }),
    defaultScopeTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default scope: full-history latest releases",
      ko: "기본 범위: 전체 히스토리 최신 릴리스",
    }),
    defaultScopeBody: pickAdminDashboardLocaleText(locale, {
      en: "This screen opens with the latest 100 release buckets across full history. Use the filters below only when you need to narrow by release, app version, or platform.",
      ko: "이 화면은 전체 히스토리 기준 최신 릴리스 버킷 100건을 먼저 엽니다. 릴리스, 앱 버전, 플랫폼 기준으로 좁혀야 할 때만 아래 필터를 사용합니다.",
    }),
    activeRelease: pickAdminDashboardLocaleText(locale, { en: "active release", ko: "활성 릴리스" }),
    from: pickAdminDashboardLocaleText(locale, { en: "From", ko: "시작" }),
    to: pickAdminDashboardLocaleText(locale, { en: "To", ko: "종료" }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    platform: pickAdminDashboardLocaleText(locale, { en: "Platform", ko: "플랫폼" }),
    apply: pickAdminDashboardLocaleText(locale, { en: "Apply filters", ko: "필터 적용" }),
    reset: pickAdminDashboardLocaleText(locale, { en: "Reset", ko: "초기화" }),
    loading: pickAdminDashboardLocaleText(locale, {
      en: "Loading release regression...",
      ko: "릴리스 회귀 데이터를 불러오는 중입니다...",
    }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "Releases denied", ko: "릴리스 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, {
      en: "Release regression read permission is missing.",
      ko: "릴리스 회귀 조회 권한이 없습니다.",
    }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "Releases failed", ko: "릴리스 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No releases", ko: "릴리스 없음" }),
    emptyBody: pickAdminDashboardLocaleText(locale, {
      en: "No regression row matches the current filter.",
      ko: "현재 필터 조건에 맞는 회귀 행이 없습니다.",
    }),
    description: pickAdminDashboardLocaleText(locale, {
      en: "Start with the hottest release bucket, decide whether the spike is isolated or broad, then branch into failed-flow investigation with the same release context.",
      ko: "가장 뜨거운 릴리스 버킷부터 읽고, 이 급증이 국소적인지 더 넓은 회귀인지 판단한 뒤 같은 릴리스 문맥으로 오류 흐름 조사에 이어집니다.",
    }),
    meta: pickAdminDashboardLocaleText(locale, { en: "Meta", ko: "조회 메타" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    sourceStatus: pickAdminDashboardLocaleText(locale, { en: "Source status", ko: "소스 상태" }),
    hotspot: pickAdminDashboardLocaleText(locale, { en: "Hotspot", ko: "핫스팟" }),
    error: pickAdminDashboardLocaleText(locale, { en: "Error", ko: "오류" }),
    noHotspots: pickAdminDashboardLocaleText(locale, { en: "No hotspots", ko: "핫스팟 없음" }),
    noHotspotsBody: pickAdminDashboardLocaleText(locale, { en: "There are no current hotspots.", ko: "현재 핫스팟이 없습니다." }),
    releaseEvidenceTitle: pickAdminDashboardLocaleText(locale, { en: "Release evidence", ko: "릴리스 증거" }),
    releaseEvidenceDesc: pickAdminDashboardLocaleText(locale, { en: "Read hotspot buckets first, then compare release rows to decide whether the same appVersion or platform is broadening the regression.", ko: "먼저 핫스팟 버킷을 읽고, 그다음 릴리스 행을 비교해 같은 appVersion 또는 플랫폼이 회귀를 넓히는지 판단합니다." }),
    deployed: pickAdminDashboardLocaleText(locale, { en: "Deployed", ko: "배포 시각" }),
    requests: pickAdminDashboardLocaleText(locale, { en: "Requests", ko: "요청 수" }),
    errorRate: pickAdminDashboardLocaleText(locale, { en: "Error Rate", ko: "오류율" }),
    topUseCase: pickAdminDashboardLocaleText(locale, { en: "Top Use Case", ko: "상위 유스케이스" }),
    topError: pickAdminDashboardLocaleText(locale, { en: "Top Error", ko: "상위 오류" }),
    jobDelta: pickAdminDashboardLocaleText(locale, { en: "Job Delta", ko: "작업 증감" }),
    ratio: pickAdminDashboardLocaleText(locale, { en: "Ratio", ko: "비율" }),
    noRows: pickAdminDashboardLocaleText(locale, { en: "No rows", ko: "행 없음" }),
    noRowsBody: pickAdminDashboardLocaleText(locale, { en: "No regression rows were returned.", ko: "조회된 회귀 행이 없습니다." }),
  };

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSearchParams(createNextSearchParams(new FormData(event.currentTarget), locale));
  }

  function handleResetToLiveRange(): void {
    setSearchParams(withAdminDashboardLocaleSearchParams(clearExplicitAdminTimeRangeSearchParams(searchParams), locale));
  }

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              {copy.title}
            </h3>
            <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6, maxWidth: "44rem" }}>
              {copy.description}
            </p>
            <AdminOperatorExplanationBlock
              screen="releases"
              slot="releases_top_hotspot"
              style={{ marginTop: "14px", maxWidth: "44rem" }}
            />
          </div>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {copy.activeRelease}: {props.dependencies.runtimeConfig.release ?? "dev-local"}
          </p>
        </div>
      </header>

      {explicitUpperBound ? (
        <AdminLiveRangeBanner
          explicitUpperBound={explicitUpperBound}
          locale={locale}
          onResetToLive={handleResetToLiveRange}
        />
      ) : null}

      <section style={cardStyle}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.defaultScopeTitle}</p>
        <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>{copy.defaultScopeBody}</p>
      </section>

      <form onSubmit={handleFilterSubmit}>
        <AdminFilterBar
          items={[
            {
              id: "from",
              label: copy.from,
              control: <input defaultValue={searchParams.get("from") ?? ""} id="from" name="from" style={baseControlStyle()} />,
            },
            {
              id: "to",
              label: copy.to,
              control: <input defaultValue={searchParams.get("to") ?? ""} id="to" name="to" style={baseControlStyle()} />,
            },
            {
              id: "release",
              label: copy.release,
              control: <input defaultValue={searchParams.get("release") ?? ""} id="release" name="release" style={baseControlStyle()} />,
            },
            {
              id: "appVersion",
              label: copy.app,
              control: <input defaultValue={searchParams.get("appVersion") ?? ""} id="appVersion" name="appVersion" style={baseControlStyle()} />,
            },
            {
              id: "platform",
              label: copy.platform,
              control: <input defaultValue={searchParams.get("platform") ?? ""} id="platform" name="platform" style={baseControlStyle()} />,
            },
          ]}
          actions={
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                style={{
                  minHeight: "40px",
                  padding: "0 14px",
                  borderRadius: "999px",
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                type="submit"
              >
                {copy.apply}
              </button>
              <AdminLocalizedLink
                to="/admin/releases"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: "40px",
                  padding: "0 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(148, 163, 184, 0.45)",
                  color: "#18212b",
                  textDecoration: "none",
                  fontWeight: 700,
                }}
              >
                {copy.reset}
              </AdminLocalizedLink>
            </div>
          }
        />
      </form>

      {state.status === "loading" || state.status === "idle" ? (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      ) : null}

      {state.status === "permission_denied" ? (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{
            screen: "releases",
            slot: "shared_error_state_guidance",
          }}
          meta={buildReleaseStateMeta(locale, query, scopeSummary)}
          requestId={state.error.requestId}
          tone="permission_denied"
        />
      ) : null}

      {state.status === "error" ? (
        <AdminErrorState
          title={copy.failedTitle}
          body={state.error.message}
          guidance={{
            screen: "releases",
            slot: "shared_error_state_guidance",
          }}
          meta={buildReleaseStateMeta(locale, query, scopeSummary)}
          requestId={state.error.requestId}
          tone="error"
        />
      ) : null}

      {state.status === "empty" ? (
        <AdminEmptyState
          title={copy.emptyTitle}
          body={copy.emptyBody}
          guidance={{
            screen: "releases",
            slot: "shared_empty_state_guidance",
          }}
          meta={buildReleaseStateMeta(locale, query, scopeSummary)}
        />
      ) : null}

      {state.status === "ready" || state.status === "partial" || state.status === "stale" ? (
        <>
          <section
            style={{
              ...cardStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", fontWeight: 700 }}>
                {copy.meta}
              </p>
              <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                {copy.generated} {state.viewModel.generatedAtLabel}
              </p>
              <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                {copy.request} {state.viewModel.requestId}
              </p>
            </div>
            <AdminStatusBadge value={toAdminFreshnessBadgeValue(state.viewModel.freshness)} />
          </section>

          <section style={{ display: "grid", gap: "12px" }}>
            <article style={cardStyle}>
              <div style={{ display: "grid", gap: "10px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.sourceStatus}
                </p>
                <AdminSourceStatusGrid
                  items={state.viewModel.sourceFreshness}
                  statusLabel={copy.sourceStatus}
                />
              </div>
            </article>
            {state.viewModel.aiRuntime ? (
              <AdminAiRuntimeSummaryStrip
                ariaLabel={state.viewModel.aiRuntime.title}
                items={state.viewModel.aiRuntime.items}
                sourceGapLabel={state.viewModel.aiRuntime.sourceGapLabel}
                title={state.viewModel.aiRuntime.title}
              />
            ) : null}
            {state.viewModel.observability ? (
              <AdminAggregateSummaryStrip
                ariaLabel={state.viewModel.observability.title}
                items={state.viewModel.observability.items}
                sourceGapLabel={state.viewModel.observability.sourceGapLabel}
                title={state.viewModel.observability.title}
              />
            ) : null}
            {state.viewModel.rowSnapshot ? (
              <AdminAggregateSummaryStrip
                ariaLabel={state.viewModel.rowSnapshot.title}
                items={state.viewModel.rowSnapshot.items}
                sourceGapLabel={state.viewModel.rowSnapshot.sourceGapLabel}
                title={state.viewModel.rowSnapshot.title}
              />
            ) : null}
            <div style={{ display: "grid", gap: "8px" }}>
              <div style={{ display: "grid", gap: "6px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.releaseEvidenceTitle}
                </p>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                  {copy.releaseEvidenceDesc}
                </p>
              </div>
              <AdminOperatorExplanationBlock
                screen="releases"
                slot="releases_table_reading_order"
              />
            </div>

            <AdminDataTable
              columns={[
                {
                  id: "release",
                  label: copy.hotspot,
                  renderCell: (row) => (
                    <div style={{ display: "grid", gap: "4px" }}>
                      <AdminLocalizedLink to={row.drilldownTo} style={{ fontWeight: 700 }}>
                        {row.release}
                      </AdminLocalizedLink>
                      <span style={{ color: "#475569", fontSize: "0.84rem" }}>{row.summary}</span>
                    </div>
                  ),
                },
                { id: "appVersion", label: copy.app, renderCell: (row) => row.appVersionLabel },
                { id: "platform", label: copy.platform, renderCell: (row) => row.platformLabel },
                { id: "topErrorCode", label: copy.error, renderCell: (row) => row.topErrorCodeLabel },
              ]}
              emptyState={<AdminEmptyState title={copy.noHotspots} body={copy.noHotspotsBody} />}
              getRowId={(row) => `${row.release}:${row.appVersionLabel}:${row.platformLabel}`}
              rows={state.viewModel.hotspots}
            />
          </section>

          <AdminDataTable
            columns={[
              {
                id: "release",
                label: copy.release,
                sortValue: (row) => row.release,
                renderCell: (row) => (
                  <AdminLocalizedLink to={row.drilldownTo} style={{ fontWeight: 700 }}>
                    {row.release}
                  </AdminLocalizedLink>
                ),
              },
              { id: "appVersion", label: copy.app, renderCell: (row) => row.appVersionLabel },
              {
                id: "deployedAt",
                label: copy.deployed,
                sortValue: (row) => row.deployedAt,
                defaultSortDirection: "desc",
                renderCell: (row) => row.deployedAtLabel,
              },
              {
                id: "requestCount",
                label: copy.requests,
                sortValue: (row) => row.requestCount,
                defaultSortDirection: "desc",
                renderCell: (row) => row.requestCountLabel,
                align: "right",
              },
              { id: "errorRate", label: copy.errorRate, renderCell: (row) => row.errorRateLabel, align: "right" },
              { id: "topFailedUseCase", label: copy.topUseCase, renderCell: (row) => row.topFailedUseCaseLabel },
              { id: "topErrorCode", label: copy.topError, renderCell: (row) => row.topErrorCodeLabel },
              { id: "jobFailureDelta", label: copy.jobDelta, renderCell: (row) => row.jobFailureDeltaLabel, align: "right" },
              { id: "dominantPlatform", label: copy.platform, renderCell: (row) => row.dominantPlatformLabel },
              { id: "dominantPlatformRatio", label: copy.ratio, renderCell: (row) => row.dominantPlatformRatioLabel, align: "right" },
            ]}
            emptyState={<AdminEmptyState title={copy.noRows} body={copy.noRowsBody} />}
            getRowId={(row) => `${row.release}:${row.appVersionLabel}`}
            rows={state.viewModel.rows}
          />
        </>
      ) : null}
    </section>
  );
}
